import { CachedValue } from "@ingka/webc-tools";
import SkapaElement from "@ingka/skapa-webc-element";
import { ChoiceItem } from "./ChoiceItem.js";
const styles = ':host{--skapa-choice-min-height:5.5rem;--skapa-choice-padding-block:1.25rem;background:#0000;display:block;list-style:none;margin:0;padding:0;width:100%}:host([size=small]){--skapa-choice-min-height:3rem;--skapa-choice-padding-block:.5rem;--skapa-choice-item-spacing:0.5rem;--skapa-choice-nested-padding:0 1.5rem 0.75rem}:host([size=medium]){--skapa-choice-min-height:4.5rem;--skapa-choice-padding-block:.75rem;--skapa-choice-item-spacing:0.75rem;--skapa-choice-nested-padding:0 1.5rem 1rem}::slotted(*){margin-bottom:var(--skapa-choice-item-spacing,1rem)}:host([leading-alignment=top]){--skapa-choice-leading-alignment:flex-start}:host([trailing-alignment=top]){--skapa-choice-trailing-alignment:flex-start}:host([subtle]){--skapa-choice-item-border-colour-selected:rgb(var(--colour-neutral-7,17,17,17));--skapa-choice-item-border-colour-selected-hover:rgb(var(--colour-neutral-7,17,17,17))}';
const defaultSize = "large";
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <slot></slot>
  `;
  return { main: template };
}
class Choice extends SkapaElement {
  constructor() {
    super();
    // Returns the assigned elements in the leading and trailing slots.
    this.getSlottedElements = () => {
      return this.shadowSlot.assignedElements({ flatten: true });
    };
    // Cached array of the assigned elements in the leading and trailing slots.
    this.slottedElements = new CachedValue(this.getSlottedElements);
    this.handleSlotChange = () => {
      this.slottedElements.updateCache = true;
      if (this.disabled)
        this.syncDisabledState();
      this.syncChoiceItems();
    };
    // Propagate local properties to assigned choice items.
    this.syncChoiceItems = () => {
      const size = this.size;
      this.slottedElements.value.forEach((element) => {
        element.size = size;
      });
    };
    // Propagate local disabled state to assigned choice items.
    this.syncDisabledState = () => {
      const disabled = this.disabled;
      this.slottedElements.value.forEach((element) => {
        element.disabled = disabled;
      });
    };
    this.gatherProperties(["subtle", "deselectable", "disabled"], this);
    const shadow = this.attachShadow({ mode: "open" });
    const templates = this.getTemplates(generateTemplate);
    shadow.append(templates.main.content.cloneNode(true));
    this.shadowSlot = shadow.querySelector("slot");
  }
  connectedCallback() {
    super.connectedCallback();
    this.syncChoiceItems();
    this.setAttribute("role", "list");
    this.addEventListener("choice-item-select", this.handleSelection);
    this.shadowRoot.addEventListener("slotchange", this.handleSlotChange);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("choice-item-select", this.handleSelection);
    this.shadowRoot.removeEventListener("slotchange", this.handleSlotChange);
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "disabled") {
      this.syncDisabledState();
    } else {
      this.syncChoiceItems();
    }
  }
  get deselectable() {
    return this.hasAttribute("deselectable");
  }
  set deselectable(val) {
    this.toggleAttribute("deselectable", !!val);
  }
  get subtle() {
    return this.hasAttribute("subtle");
  }
  set subtle(val) {
    this.toggleAttribute("subtle", !!val);
  }
  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(val) {
    this.toggleAttribute("disabled", !!val);
  }
  get size() {
    return this.getAttribute("size") || defaultSize;
  }
  set size(size) {
    if (size === defaultSize) {
      this.removeAttribute("size");
    } else {
      this.setAttribute("size", size);
    }
  }
  get leadingAlignment() {
    return this.getAttribute("leading-alignment") || "center";
  }
  set leadingAlignment(align) {
    if (align === "top")
      this.setAttribute("leading-alignment", align);
    else
      this.removeAttribute("leading-alignment");
  }
  get trailingAlignment() {
    return this.getAttribute("trailing-alignment") || "center";
  }
  set trailingAlignment(align) {
    if (align === "top")
      this.setAttribute("trailing-alignment", align);
    else
      this.removeAttribute("trailing-alignment");
  }
  handleSelection(ev) {
    const target = ev.target;
    this.slottedElements.value.forEach(
      (element) => element.selected = element === target && // Mark it selected if it is the child clicked
      !(this.deselectable && // and it is not deselectable
      target.selected)
      // or if it is deselectable, then deselect if already selected
    );
  }
}
Choice.skapaTagName = "choice";
Choice.skapaDependencies = [ChoiceItem];
Choice.observedAttributes = ["disabled", "size"];
var Choice_default = Choice;
export {
  Choice,
  Choice_default as default
};
